import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swords, Pickaxe, Home, Wheat, Server, Users, Search, ArrowRight, type LucideIcon } from 'lucide-react';
import { apiService, ServerDTO, OnlineSummaryResponse, OnlinePlayerDTO } from '@/services/api.service';
import styles from './ConsolePage.module.scss';

interface FamilyMeta {
  label: string;
  icon: LucideIcon;
  order: number;
}

// Métadonnées d'affichage des familles connues (icône + libellé + ordre).
// Toute autre famille de jeu est détectée dynamiquement (icône générique).
const FAMILY_META: Record<string, FamilyMeta> = {
  faction: { label: 'Faction', icon: Swords, order: 1 },
  mining: { label: 'Minage', icon: Pickaxe, order: 2 },
  lobby: { label: 'Lobby', icon: Home, order: 3 },
  farmland: { label: 'Farmland', icon: Wheat, order: 4 },
};

// Préfixes d'infrastructure à masquer (non-jeu).
const HIDDEN_PREFIXES = ['proxy', 'build', 'cdn', 'warzone', 'beta'];

/** Famille = préfixe du nom, sans le suffixe numérique (mining-7 -> mining). */
function familyKey(server: ServerDTO): string {
  const base = server.name.toLowerCase().trim().replace(/[-_\s]*\d+$/, '');
  return base === 'minage' ? 'mining' : base;
}

function isHiddenFamily(key: string): boolean {
  return HIDDEN_PREFIXES.some((p) => key.startsWith(p));
}

function familyMeta(key: string): FamilyMeta {
  return FAMILY_META[key] ?? { label: key.charAt(0).toUpperCase() + key.slice(1), icon: Server, order: 99 };
}

export function ConsolePage() {
  const navigate = useNavigate();
  const [servers, setServers] = useState<ServerDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Player sync data
  const [playerSummary, setPlayerSummary] = useState<OnlineSummaryResponse | null>(null);
  const [onlinePlayers, setOnlinePlayers] = useState<OnlinePlayerDTO[]>([]);

  useEffect(() => {
    loadServers();
    loadPlayerData();

    // Refresh player data every 15 seconds
    const interval = setInterval(loadPlayerData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadPlayerData = async () => {
    const [summaryResult, playersResult] = await Promise.all([
      apiService.getOnlineSummary(),
      apiService.getOnlinePlayers(),
    ]);

    if (summaryResult.data) {
      setPlayerSummary(summaryResult.data);
    }

    if (playersResult.data) {
      setOnlinePlayers(playersResult.data.players);
    }
  };

  const loadServers = async () => {
    setLoading(true);
    setError(null);

    const result = await apiService.getAllServers();

    if (result.error) {
      console.error('API failed:', result.error);
      setError(`Erreur API: ${result.error}`);
      setServers([]);
    } else if (result.data) {
      // On masque l'infra (proxy/build/cdn...) ; les familles de jeu sont détectées dynamiquement
      const gameServers = result.data.servers.filter((s) => !isHiddenFamily(familyKey(s)));
      console.log('Servers loaded:', gameServers.length);
      setServers(gameServers);
    }

    setLoading(false);
  };

  const handleServerClick = (server: ServerDTO) => {
    navigate(`/console/${server.identifier}`);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return styles.statusRunning;
      case 'stopped':
      case 'offline':
        return styles.statusStopped;
      case 'starting':
        return styles.statusStarting;
      case 'stopping':
        return styles.statusStopping;
      default:
        return styles.statusUnknown;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return 'En ligne';
      case 'stopped':
      case 'offline':
        return 'Arrêté';
      case 'starting':
        return 'Démarrage...';
      case 'stopping':
        return 'Arrêt...';
      default:
        return 'Inconnu';
    }
  };

  const filteredServers = servers.filter((server) => {
    // Filtre par recherche
    const matchesSearch =
      server.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      server.identifier.toLowerCase().includes(searchQuery.toLowerCase());

    // Filtre par statut (offline = stopped)
    const normalizedStatus = server.status?.toLowerCase() === 'offline' ? 'stopped' : server.status?.toLowerCase();
    const matchesStatus = statusFilter === 'all' || normalizedStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Regroupe dynamiquement les serveurs filtrés par famille détectée
  const grouped = (() => {
    const byKey = new Map<string, ServerDTO[]>();
    for (const s of filteredServers) {
      const k = familyKey(s);
      const arr = byKey.get(k);
      if (arr) arr.push(s);
      else byKey.set(k, [s]);
    }
    return [...byKey.entries()]
      .map(([key, list]) => ({ key, list, meta: familyMeta(key) }))
      .sort((a, b) => a.meta.order - b.meta.order || a.key.localeCompare(b.key));
  })();

  // Helper to get player count for a specific server
  const getServerPlayerCount = (serverName: string): number => {
    // Normalize server name for comparison
    const normalizedName = serverName.toLowerCase();
    return onlinePlayers.filter((p) => {
      const playerServer = p.serverName?.toLowerCase() || '';
      return playerServer === normalizedName || playerServer.includes(normalizedName) || normalizedName.includes(playerServer);
    }).length;
  };

  // Calculer les stats - use real-time player data for total
  const totalPlayers = playerSummary?.totalPlayers ?? servers.reduce((acc, s) => acc + s.playerCount, 0);
  const onlineServers = servers.filter((s) => s.status?.toLowerCase() === 'running').length;

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div>
            <h1 className={styles.title}>Console</h1>
            <p className={styles.subtitle}>
              Gérez vos serveurs de jeu en temps réel
            </p>
          </div>
        </div>

        {/* Stats globales */}
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <span className={styles.statIcon}><Server size={22} strokeWidth={2} /></span>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{onlineServers}/{servers.length}</span>
              <span className={styles.statLabel}>Serveurs en ligne</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statIcon}><Users size={22} strokeWidth={2} /></span>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{totalPlayers.toLocaleString()}</span>
              <span className={styles.statLabel}>Joueurs connectés</span>
            </div>
          </div>
        </div>
      </header>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* Filtres */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Rechercher un serveur..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className={styles.statusSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="running">En ligne</option>
          <option value="stopped">Arrêtés</option>
          <option value="starting">En démarrage</option>
        </select>
        <button className={styles.refreshButton} onClick={loadServers}>
          Rafraîchir
        </button>
      </div>

      {/* Serveurs regroupés par famille (détection dynamique) */}
      {grouped.map(({ key, list, meta }) => {
        const FamIcon = meta.icon;
        return (
          <section key={key} className={styles.familySection}>
            <h2 className={styles.familyTitle}>
              <FamIcon size={18} strokeWidth={2} />
              <span>{meta.label}</span>
              <span className={styles.familyCount}>{list.length}</span>
            </h2>
            <div className={styles.serverGrid}>
              {list.map((server) => (
                <div
                  key={server.identifier}
                  className={`${styles.serverCard} ${server.suspended ? styles.suspended : ''}`}
                  onClick={() => handleServerClick(server)}
                >
                  <div className={styles.serverHeader}>
                    <span className={styles.serverIcon}><FamIcon size={20} strokeWidth={2} /></span>
                    <div className={styles.serverTitleBlock}>
                      <h3 className={styles.serverName}>{server.name}</h3>
                      <span className={styles.serverIdentifier}>{server.identifier}</span>
                    </div>
                    <span className={`${styles.statusBadge} ${getStatusColor(server.status)}`}>
                      {getStatusLabel(server.status)}
                    </span>
                  </div>

                  {server.description && (
                    <p className={styles.serverDescription}>{server.description}</p>
                  )}

                  <div className={styles.serverFooter}>
                    <div className={styles.playerCount}>
                      <Users size={14} className={styles.playerIcon} />
                      <span className={styles.playerValue}>
                        {(() => {
                          const count = getServerPlayerCount(server.name);
                          return `${count} joueur${count !== 1 ? 's' : ''}`;
                        })()}
                      </span>
                    </div>
                    <span className={styles.viewDetails}>
                      Voir les détails <ArrowRight size={14} />
                    </span>
                  </div>

                  {server.suspended && (
                    <div className={styles.suspendedOverlay}>
                      <span>Suspendu</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {filteredServers.length === 0 && (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}><Search size={32} strokeWidth={1.5} /></span>
          <p>Aucun serveur trouvé</p>
          <span className={styles.emptyHint}>
            Essayez de modifier vos filtres de recherche
          </span>
        </div>
      )}
    </div>
  );
}
